import { dynamicUiConfigResponse } from "../data/dynamicUiConfigResponse";

const fallbackUiConfig = dynamicUiConfigResponse.data.ui_config;

const slugify = (value, fallback = "theme") =>
  String(value || fallback)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || fallback;

const normalizeThemes = (themes, defaultThemeId = fallbackUiConfig.default_theme_id) => {
  if (Array.isArray(themes)) {
    return themes.reduce((themeMap, theme, index) => {
      const themeId = theme?.id || theme?.theme_id || theme?.themeId || slugify(theme?.name, `theme_${index}`);
      return {
        ...themeMap,
        [themeId]: {
          id: themeId,
          ...theme,
        },
      };
    }, {});
  }

  if (themes?.colors || themes?.gradients || themes?.radius) {
    const themeId = themes.id || themes.theme_id || themes.themeId || defaultThemeId || "marketplace";

    return {
      [themeId]: {
        id: themeId,
        ...themes,
      },
    };
  }

  return themes || fallbackUiConfig.themes;
};

const normalizeUiConfig = (configurationData) => {
  const isDirectUiConfig = Boolean(
    configurationData?.screens ||
      configurationData?.themes ||
      configurationData?.theme_data ||
      configurationData?.themeData ||
      configurationData?.theme
  );
  const uiConfig =
    configurationData?.ui_config ||
    configurationData?.uiConfig ||
    configurationData?.app_ui_config ||
    configurationData?.data?.ui_config ||
    configurationData?.data?.uiConfig ||
    configurationData?.response_data?.ui_config ||
    configurationData?.response_data?.uiConfig ||
    (isDirectUiConfig ? configurationData : null);

  if (!uiConfig) {
    return fallbackUiConfig;
  }

  const defaultThemeId =
    uiConfig.default_theme_id ||
    uiConfig.defaultThemeId ||
    uiConfig.theme_id ||
    uiConfig.themeId ||
    fallbackUiConfig.default_theme_id;
  const themes = normalizeThemes(
    uiConfig?.themes ||
      uiConfig?.theme_data ||
      uiConfig?.themeData ||
      uiConfig?.theme ||
      configurationData?.theme_data ||
      configurationData?.themeData ||
      configurationData?.theme,
    defaultThemeId
  );

  return {
    ...uiConfig,
    default_theme_id: defaultThemeId,
    themes,
  };
};

export const getUiConfig = (configurationData) => normalizeUiConfig(configurationData);

export const getThemeById = (uiConfig, themeId) => {
  const themes = uiConfig?.themes || {};

  return (
    themes?.[themeId] ||
    themes?.[uiConfig?.default_theme_id] ||
    themes?.marketplace ||
    fallbackUiConfig.themes.marketplace
  );
};

export const getScreenConfig = (configurationData, screenKey = "home") => {
  const uiConfig = getUiConfig(configurationData);
  const screens = uiConfig?.screens || uiConfig?.screen_data || uiConfig?.screenData || {};
  const screen =
    screens?.[screenKey] ||
    uiConfig?.[screenKey] ||
    fallbackUiConfig.screens?.[screenKey] ||
    fallbackUiConfig.screens.home;
  const theme = screen?.theme || getThemeById(uiConfig, screen?.theme_id || screen?.themeId);

  return {
    ...screen,
    version: uiConfig?.version || fallbackUiConfig.version,
    theme,
    themes: uiConfig?.themes || fallbackUiConfig.themes,
  };
};

export const buildScreenPayload = (configurationData, screenKey = "home") => {
  const screen = getScreenConfig(configurationData, screenKey);

  return {
    screen: screen.screen_key || screenKey,
    version: screen.version,
    theme: screen.theme,
    themes: screen.themes,
    header: screen.header || screen.topBar || screen.app_bar || screen.appBar,
    sections: screen.sections || [],
    bottomNav:
      screen.bottomNav ||
      screen.bottom_nav ||
      screen.footerNav ||
      screen.footer_nav ||
      screen.footer?.items ||
      [],
    screenConfig: screen,
  };
};

export { fallbackUiConfig };
